import {
  ChangeDetectionStrategy,
  Component,
  type OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from '@fino-ui/button';
import { ModalService } from '@fino-ui/modal';
import { TranslationModule } from '@fino-ui/translation';
import { merge } from 'lodash-es';
import { of } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { CachingService } from 'src/app/core/common/caching.service';
import { ActionBoxComponent } from '../../components/action-box/action-box.component';
import { ActivateProductSwitchComponent } from '../../components/activate-product-switch/activate-product-switch.component';
import { HeaderComponent } from '../../components/header/header.component';
import { CockpitService } from './cockpit.service';
import { BasicInfoComponent } from './components/basic-info/basic-info.component';
import { KWSDeActivateComponent } from './components/deactivate/deactivate.component';
import { ImprintComponent } from './components/imprint/imprint.component';
import { LegalComponent } from './components/legal/legal.component';
import { SustainabilityComponent } from './components/sustainability/sustainability.component';
import type { DnsCheck, KWSConfig, Legal, ShowPaperSaved } from './kws.model';
import { KwsService } from './kws.service';

export const KWS_MODEL = 'kwsSettings';

@Component({
  selector: 'app-kws',
  templateUrl: './kws.component.html',
  styleUrl: './kws.component.sass',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    HeaderComponent,
    ActivateProductSwitchComponent,
    ActionBoxComponent,
    TranslationModule,
    ButtonModule,
    BasicInfoComponent,
  ],
})
export class KwsComponent implements OnInit {
  kwsSettings: KWSConfig;

  hasLinkToImprint = signal(true);
  hasNachlass = signal(false);
  dns = signal<DnsCheck | undefined>(undefined);

  constructor(
    private kwsService: KwsService,
    private ngxModalService: ModalService,
    private appService: AppService,
    private route: ActivatedRoute,
    private router: Router,
    private cachingService: CachingService,
    private cockpitService: CockpitService,
  ) {}

  get isActive() {
    return !this.kwsSettings?.features.kwsDisabled;
  }

  ngOnInit(): void {
    // only a hint, so a failing check is not shown to the user
    this.kwsService.fetchDns().subscribe({
      next: (dns) => this.dns.set(dns),
      error: () => this.dns.set(undefined),
    });

    this.kwsService.fetchKWS().subscribe((config: KWSConfig) => {
      if (this.isAtruvia) {
        config.links.anonymizedTesting =
          'https://10090902.test.meinkontowechsel.de/';
      }

      this.cacheDataModel(config);

      this.hasLinkToImprint.set(
        this.isAtruvia &&
          this.hasExternalImprint(this.kwsSettings?.theme?.links?.imprint),
      );

      this.hasNachlass.set(
        this.kwsSettings?.features?.appMode?.modes.includes('nachlass'),
      );

      this.route.queryParams?.subscribe((p) => {
        if (p.modal) {
          this[p.modal]();
          // remove queryparams silently
          this.router.navigate([], {});
        }
      });
    });
  }

  toggleActiveState($event) {
    this.kwsSettings.features.kwsDisabled = !$event;
    const approved = $event
      ? of(true)
      : this.ngxModalService.addModal<boolean>(KWSDeActivateComponent, {});

    approved.subscribe((result) => {
      if (!result) {
        this.kwsSettings.features.kwsDisabled = $event;
        return;
      }

      const request = $event
        ? this.kwsService.enableKWS()
        : this.kwsService.disableKWS();

      request.subscribe({
        next: () => {
          this.appService.success(
            `kws.${$event ? 'activate' : 'deactivate'}.success`,
          );
        },
        error: () => {
          this.appService.error(
            `kws.${$event ? 'activate' : 'deactivate'}.error`,
          );
          this.kwsSettings.features.kwsDisabled = $event;
        },
      });
    });
  }

  cacheDataModel(resp: KWSConfig, overwrite = false) {
    this.kwsSettings = this.cachingService.cacheDataModel(
      resp,
      KWS_MODEL,
      overwrite,
    );
  }

  openDashboard() {
    this.kwsService.openDashboard();
  }

  openCockpit() {
    this.cockpitService.openKwsCockpit();
  }

  get isAtruvia() {
    return this.appService.isAtruvia;
  }

  openImprintModal() {
    this.ngxModalService
      .addModal(ImprintComponent, {
        displayName: this.kwsSettings.displayName,
        imprint: this.kwsSettings.theme.links.imprint,
      })
      .subscribe((result: any) => {
        if (result) {
          this.kwsSettings = merge(this.kwsSettings, result);

          this.kwsService.changeKWSSettings(this.kwsSettings).subscribe({
            next: () => {
              this.kwsSettings.theme.links.imprint = result.imprint;
              this.cacheDataModel(this.kwsSettings, true);

              this.hasLinkToImprint.set(
                this.isAtruvia &&
                  this.hasExternalImprint(this.kwsSettings?.imprint),
              );
              this.appService.success('kwsImprint.info.success');
            },
            error: () => {
              this.appService.error('kwsImprint.info.error');
            },
          });
        }
      });
  }

  openLegalModal() {
    const privacyOfficer = merge(
      { bankaddress: this.kwsSettings.legal.bankaddress },
      this.kwsSettings.legal.privacyOfficer,
    );

    this.ngxModalService
      .addModal<Legal>(LegalComponent, privacyOfficer as any)
      .subscribe((result) => {
        if (result) {
          const patchedConfig = merge(this.kwsSettings, { legal: result });

          this.kwsService.changeKWSSettings(patchedConfig).subscribe({
            next: () => {
              this.cacheDataModel(patchedConfig, true);
              this.appService.success('kws.actions.privacy.success');
            },
            error: () => {
              this.appService.error('kws.actions.privacy.error');
            },
          });
        }
      });
  }

  hasExternalImprint(maybeUrl: string) {
    return !!maybeUrl && maybeUrl.includes('www');
  }

  get isCockpitEnabled() {
    return !!this.kwsSettings?.features?.kontowechselCockpit?.enabled;
  }

  get featurePaperSaved() {
    return this.kwsSettings?.features?.showPaperSaved?.isActive;
  }

  openSustainabilityModa() {
    this.ngxModalService
      .addModal<ShowPaperSaved>(SustainabilityComponent, {
        features: this.kwsSettings.features,
      })
      .subscribe((result) => {
        if (result) {
          const { features } = this.kwsSettings;
          features.showPaperSaved = result;

          this.kwsService.changeKWSSettings({ features: features }).subscribe({
            next: () => {
              this.cacheDataModel(
                merge(this.kwsSettings, {
                  features: {
                    showPaperSaved: result,
                  },
                }),
                true,
              );
              this.appService.success('kws.actions.nachhaltigkeit.success');
            },
            error: () => {
              this.appService.error('kws.actions.nachhaltigkeit.error');
            },
          });
        }
      });
  }
}
